import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Calendar, DatePicker } from 'rsuite';
import enUS from 'rsuite/locales/en_US';
import {
  countDaysInclusive,
  formatLongDate,
  isSameDay,
  keyForDate,
  normalizeDateOnly,
  parseLocalDateOnly,
} from '../../utils/timeOffDates';
import '../../pages/TimeOffPage.css';

// Months as one running number, so "which way did it move" is a subtraction.
const monthIndexOf = (date) => date.getFullYear() * 12 + date.getMonth();

// How the month grid slides: the outgoing month leaves one way as the next
// one arrives from the other. Part of the width rather than all of it, with a
// fade, so a grid this big glides rather than whips across.
const SLIDE_DISTANCE = '40%';
const SLIDE_OPTIONS = { duration: 320, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' };

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/**
 * The large range-picking calendar the time-off flows open with: click a start
 * day then an end day, or type them into the pickers underneath. Each day
 * carries its close dates, the employee's own pending requests and everyone's
 * approved time off, so a range can be chosen around them.
 *
 * Shared by Create Request and the Vacation Planner so both start from the
 * same picture of the calendar.
 *
 * `employeeId` is whose pending requests are marked. `editingRequest`, when
 * modifying a request, is left out of the signals and marked as the existing
 * request instead.
 */
export default function TimeOffRangeCalendar({
  rangeStart,
  rangeEnd,
  onRangeChange,
  requests = [],
  closeDates = [],
  employeeId = null,
  editingRequest = null,
}) {
  /*
    Which month is on screen is decided here, not by rsuite.

    Left to itself rsuite moves to whatever date it was last given or clicked,
    so picking an end date among the next month's greyed-out days threw the
    whole view forward a month - away from the range being picked. Here the
    view moves only when somebody navigates (arrows, Today, the month picker),
    types a date into the pickers below, or the range is set from outside (a
    request being modified, a saved plan opened). `viewDate` goes to rsuite as
    its value purely to say which month to show.
  */
  const [viewDate, setViewDate] = useState(() => rangeStart || rangeEnd || normalizeDateOnly(new Date()));

  // The range as this component last set it, so the effect further down can
  // tell a click here (stay put) from a range set from outside (go to it).
  const rangeKey = (start, end) => `${start ? keyForDate(start) : ''}|${end ? keyForDate(end) : ''}`;
  const lastOwnRange = useRef(rangeKey(rangeStart, rangeEnd));

  const setRange = (start, end) => {
    lastOwnRange.current = rangeKey(start, end);
    onRangeChange(start, end);
  };

  /*
    Sliding between months.

    rsuite swaps the month in place, so the old grid is gone by the time the
    new one is drawn. beginSlide runs just before that - from onMonthChange,
    which rsuite calls before it re-renders - and lays a copy of the outgoing
    grid (the "ghost") over the calendar. Once the new month is in the DOM,
    the layout effect slides the ghost out and the new grid in, then drops the
    ghost.

    `shownMonth` follows which month is on screen, so the direction is known
    whatever moved it.
  */
  const calendarRef = useRef(null);
  const shownMonth = useRef(monthIndexOf(viewDate));
  const pendingSlide = useRef(null);
  const [slideTick, setSlideTick] = useState(0);

  const beginSlide = (nextDate) => {
    if (!nextDate) return;
    const next = monthIndexOf(nextDate);
    const previous = shownMonth.current;
    shownMonth.current = next;
    if (next === previous || prefersReducedMotion()) return;

    const root = calendarRef.current;
    const body = root?.querySelector('.rs-calendar-body');
    pendingSlide.current?.ghost?.remove();

    let ghost = null;
    if (root && body && typeof body.animate === 'function') {
      ghost = body.cloneNode(true);
      ghost.classList.add('timeoff-calendar-ghost');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.style.top = `${body.offsetTop}px`;
      ghost.style.left = `${body.offsetLeft}px`;
      ghost.style.width = `${body.offsetWidth}px`;
      ghost.style.height = `${body.offsetHeight}px`;
      root.appendChild(ghost);
    }

    pendingSlide.current = { direction: next > previous ? 1 : -1, ghost };
    setSlideTick((tick) => tick + 1);
  };

  useLayoutEffect(() => {
    const slide = pendingSlide.current;
    if (!slide) return;
    pendingSlide.current = null;

    const body = calendarRef.current?.querySelector('.rs-calendar-body');
    const from = `translateX(${slide.direction > 0 ? '' : '-'}${SLIDE_DISTANCE})`;
    const to = `translateX(${slide.direction > 0 ? '-' : ''}${SLIDE_DISTANCE})`;

    body?.animate?.(
      [{ transform: from, opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }],
      SLIDE_OPTIONS
    );

    const { ghost } = slide;
    if (ghost) {
      const leaving = ghost.animate(
        [{ transform: 'translateX(0)', opacity: 1 }, { transform: to, opacity: 0 }],
        SLIDE_OPTIONS
      );
      leaving.onfinish = () => ghost.remove();
      leaving.oncancel = () => ghost.remove();
    }
  }, [slideTick]);

  // Moves the view to the month holding `date`, sliding if it is a new one.
  const showMonthOf = (date) => {
    if (!date) return;
    beginSlide(date);
    setViewDate(date);
  };

  // A range set from outside brings its start into view. One this component
  // set itself - a click on the grid - leaves the view where it is.
  const currentRangeKey = rangeKey(rangeStart, rangeEnd);
  useEffect(() => {
    if (currentRangeKey === lastOwnRange.current) return;
    lastOwnRange.current = currentRangeKey;
    showMonthOf(rangeStart || rangeEnd);
    // Keyed on the range itself; showMonthOf only reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRangeKey]);

  /*
    A click on one of the next or previous month's greyed-out days. rsuite
    would select it and jump to its month; it is taken here first instead and
    selected without moving. Each cell carries its date in a hidden tag (see
    renderCell), so it is read back exactly rather than parsed from a label.
  */
  const onGridClickCapture = (event) => {
    const cell = event.target?.closest?.('.rs-calendar-table-cell-un-same-month');
    if (!cell) return;
    const date = parseLocalDateOnly(cell.querySelector('[data-cell-date]')?.getAttribute('data-cell-date'));
    if (!date) return;
    event.stopPropagation();
    onCalendarSelect(date);
  };

  // Never leave a ghost behind.
  useEffect(() => () => pendingSlide.current?.ghost?.remove(), []);

  const onCalendarSelect = (date) => {
    const picked = normalizeDateOnly(date);
    if (!picked) return;

    if (!rangeStart || rangeEnd) {
      setRange(picked, null);
      return;
    }

    if (picked < rangeStart) {
      setRange(picked, rangeStart);
      return;
    }

    setRange(rangeStart, picked);
  };

  // A date typed into a picker is somewhere the person wants to look, so the
  // view goes to it.
  const onChangeStartDate = (value) => {
    const nextStart = normalizeDateOnly(value);
    if (!nextStart) {
      setRange(null, null);
      return;
    }
    setRange(nextStart, !rangeEnd || rangeEnd < nextStart ? nextStart : rangeEnd);
    showMonthOf(nextStart);
  };

  const onChangeEndDate = (value) => {
    const nextEnd = normalizeDateOnly(value);
    if (!nextEnd) {
      setRange(rangeStart, null);
      return;
    }
    setRange(!rangeStart || nextEnd < rangeStart ? nextEnd : rangeStart, nextEnd);
    showMonthOf(nextEnd);
  };

  const clearSelection = () => setRange(null, null);

  const inRange = (date) => {
    if (!rangeStart || !rangeEnd) return false;
    const d = normalizeDateOnly(date);
    return d >= rangeStart && d <= rangeEnd;
  };

  const isStart = (date) => !!rangeStart && isSameDay(normalizeDateOnly(date), rangeStart);
  const isEnd = (date) => !!rangeEnd && isSameDay(normalizeDateOnly(date), rangeEnd);

  const rangeLabel = useMemo(() => {
    if (!rangeStart && !rangeEnd) return 'Select a start date, then select an end date.';
    if (rangeStart && !rangeEnd) return `Start selected: ${formatLongDate(rangeStart)}. Select an end date.`;
    return `${formatLongDate(rangeStart)} to ${formatLongDate(rangeEnd)}`;
  }, [rangeStart, rangeEnd]);

  const selectedDayCount = useMemo(
    () => countDaysInclusive(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  const hasSelectedDays = Boolean(rangeStart || rangeEnd);
  const today = normalizeDateOnly(new Date());
  const editingRequestId = editingRequest?.id ? String(editingRequest.id) : null;

  const daySignals = useMemo(() => {
    const map = new Map();

    const ensureRow = (key) => {
      if (!map.has(key)) {
        map.set(key, {
          approvedOtherEntries: [],
          hasMyPending: false,
          hasEditingRequest: false,
          closeDateNames: [],
        });
      }
      return map.get(key);
    };

    for (const request of requests || []) {
      if (editingRequestId && String(request.id) === editingRequestId) continue;

      const start = parseLocalDateOnly(request.start_date);
      const end = parseLocalDateOnly(request.end_date);
      if (!start || !end || end < start) continue;

      const status = String(request.status || '').toLowerCase();
      const isMine = employeeId && String(request.employee_id) === String(employeeId);
      const isApproved = status === 'approved';
      const isMyPending = status === 'pending' && isMine;
      if (!isApproved && !isMyPending) continue;

      const firstName = request.first_name || request.employee_first_name || '';
      const lastName = request.last_name || request.employee_last_name || '';
      const fullName = `${String(firstName).trim()} ${String(lastName).trim()}`.trim();
      const userLabel = fullName || request.email || request.employee_email || 'Unknown User';
      const approvedEntryLabel = `Time off - ${userLabel}`;

      for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        const key = keyForDate(cursor);
        if (!key) continue;
        const row = ensureRow(key);
        if (isApproved && !row.approvedOtherEntries.includes(approvedEntryLabel)) {
          row.approvedOtherEntries.push(approvedEntryLabel);
        }
        if (isMyPending) row.hasMyPending = true;
      }
    }

    if (editingRequest) {
      const start = parseLocalDateOnly(editingRequest.start_date);
      const end = parseLocalDateOnly(editingRequest.end_date);
      if (start && end && end >= start) {
        for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
          const key = keyForDate(cursor);
          if (!key) continue;
          const row = ensureRow(key);
          row.hasEditingRequest = true;
        }
      }
    }

    for (const closeDate of closeDates || []) {
      const ranges = Array.isArray(closeDate?.resolved_dates) ? closeDate.resolved_dates : [];
      for (const range of ranges) {
        const start = parseLocalDateOnly(range?.startDate);
        const end = parseLocalDateOnly(range?.endDate || range?.startDate);
        if (!start || !end || end < start) continue;

        for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
          const key = keyForDate(cursor);
          if (!key) continue;
          const row = ensureRow(key);
          const closeDateName = String(closeDate?.name || '').trim();
          if (closeDateName && !row.closeDateNames.includes(closeDateName)) {
            row.closeDateNames.push(closeDateName);
          }
        }
      }
    }

    return map;
  }, [closeDates, editingRequest, editingRequestId, employeeId, requests]);

  return (
    <>
      <div className="timeoff-range-label">{rangeLabel}</div>

      <div className="timeoff-large-calendar-wrap" onClickCapture={onGridClickCapture}>
        {/* Rides on the calendar's own header row, left of its Today
            button: rsuite renders that button itself and takes no toolbar
            prop, so this is laid over the row rather than rendered into
            it. Keeps the legend from costing a band of its own. */}
        <div className="timeoff-calendar-toolbar">
          <div className="timeoff-calendar-legend">
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-close" />Close Date</span>
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-existing" />Existing Request</span>
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-approved" />Approved</span>
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-pending" />Pending</span>
          </div>

          {hasSelectedDays ? (
            <button type="button" className="bdc-btn bdc-btn--sm" onClick={clearSelection}>
              <i className="bi bi-x-lg" aria-hidden="true" />
              Clear
            </button>
          ) : null}
        </div>

        <Calendar
          ref={calendarRef}
          locale={enUS}
          isoWeek={false}
          weekStart={0}
          bordered
          // Only which month to show - see viewDate. The range is drawn by
          // renderCell.
          value={viewDate}
          onSelect={onCalendarSelect}
          onMonthChange={showMonthOf}
          className="timeoff-large-calendar"
          renderCell={(date) => {
            const signal = daySignals.get(keyForDate(date));
            const closeDateNames = signal?.closeDateNames || [];
            const approvedOtherEntries = signal?.approvedOtherEntries || [];
            const hasMyPending = Boolean(signal?.hasMyPending);
            const hasEditingRequest = Boolean(signal?.hasEditingRequest);
            const isTodayCell = isSameDay(normalizeDateOnly(date), today);
            const selectedStart = isStart(date);
            const selectedEnd = isEnd(date);
            const selectedInRange = inRange(date);
            const previousDate = normalizeDateOnly(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1));
            const nextDate = normalizeDateOnly(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1));
            const previousInRange = inRange(previousDate);
            const nextInRange = inRange(nextDate);
            const isWeekStart = date.getDay() === 0;
            const isWeekEnd = date.getDay() === 6;
            const rangeSegmentStart = selectedInRange && (isWeekStart || !previousInRange);
            const rangeSegmentEnd = selectedInRange && (isWeekEnd || !nextInRange);
            const signalEntries = [
              ...(hasEditingRequest ? [{ type: 'existing', label: 'Existing request' }] : []),
              ...(hasMyPending ? [{ type: 'pending', label: 'Pending' }] : []),
              ...closeDateNames.map((label) => ({ type: 'close', label })),
              ...approvedOtherEntries.map((label) => ({ type: 'approved', label })),
            ];
            const visibleSignalEntries = signalEntries.slice(0, 2);
            const remainingSignalCount = signalEntries.length - visibleSignalEntries.length;

            // The cell's own date, for onGridClickCapture to read back.
            const dateTag = <span hidden data-cell-date={keyForDate(date)} />;

            if (!selectedStart && !selectedEnd && !selectedInRange && !isTodayCell && signalEntries.length === 0) {
              return dateTag;
            }

            return (
              <div className="timeoff-range-cell-markers">
                {dateTag}
                {selectedInRange ? (
                  <span
                    className={`timeoff-range-outline ${rangeSegmentStart ? 'timeoff-range-outline-start' : ''} ${rangeSegmentEnd ? 'timeoff-range-outline-end' : ''}`.trim()}
                  />
                ) : null}

                {selectedInRange ? <i className="bi bi-check2 timeoff-selected-check" aria-hidden="true" /> : null}

                {isTodayCell ? <span className="timeoff-marker timeoff-marker-today timeoff-marker-today-corner">Today</span> : null}

                <div className="timeoff-range-top-badges">
                  {selectedStart ? <span className="timeoff-marker timeoff-marker-start">Start</span> : null}
                  {selectedEnd ? <span className="timeoff-marker timeoff-marker-end">End</span> : null}
                </div>

                {visibleSignalEntries.length > 0 ? (
                  <div className="timeoff-calendar-info-lines">
                    {visibleSignalEntries.map((entry, index) => (
                      <div key={`${entry.type}-${entry.label}-${index}`} className="timeoff-calendar-info-line" title={entry.label}>
                        <span
                          className={`timeoff-calendar-dot ${
                            entry.type === 'existing'
                              ? 'timeoff-calendar-dot-existing'
                              : entry.type === 'close'
                              ? 'timeoff-calendar-dot-close'
                              : entry.type === 'approved'
                                ? 'timeoff-calendar-dot-approved'
                                : 'timeoff-calendar-dot-pending'
                          }`}
                        />
                        <span className="timeoff-calendar-info-line-text">{entry.label}</span>
                      </div>
                    ))}
                    {remainingSignalCount > 0 ? (
                      <div className="timeoff-calendar-info-line text-muted">+{remainingSignalCount} more</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          }}
        />
      </div>

      <div className="row g-3 mt-1">
        <div className="col-12 col-md-6">
          <div className="text-muted mb-1">Start Date</div>
          <DatePicker
            locale={enUS}
            oneTap
            isoWeek={false}
            weekStart={0}
            placement="topStart"
            format="MM/dd/yyyy"
            value={rangeStart}
            onChange={onChangeStartDate}
            cleanable
            style={{ width: '100%' }}
          />
        </div>

        <div className="col-12 col-md-6">
          <div className="text-muted mb-1">End Date</div>
          <DatePicker
            locale={enUS}
            oneTap
            isoWeek={false}
            weekStart={0}
            placement="topStart"
            format="MM/dd/yyyy"
            value={rangeEnd}
            onChange={onChangeEndDate}
            cleanable
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="text-muted text-center mt-2" style={{ fontSize: 12 }}>
        Days selected: {selectedDayCount}
      </div>
    </>
  );
}
